import type { ReactNode } from 'react';
import clsx from 'clsx';
import './Layout.css';

/* ------------------------------------------------------------------ *
 * The working screen has one shape, used by every module:
 *
 *   ┌─ PageHeader ───────────────────────────────────────────────┐
 *   │ eyebrow · title                            [actions]       │
 *   ├─ Toolbar ──────────────────────────────────────────────────┤
 *   │ [filters]                                  [search][view]  │
 *   ├──────────────────────────────┬─────────────────────────────┤
 *   │  the list — never replaced   │  the manifest — what you    │
 *   │  by the detail view          │  selected, open beside it   │
 *   └──────────────────────────────┴─────────────────────────────┘
 *
 * The list never disappears. In this business you work down a list of
 * 60 orders acting on each one; a full-page detail route that throws
 * away your scroll position and your filters is the difference
 * between an hour and an afternoon. Below 1100px the manifest becomes
 * a Sheet — but on a desk it is always beside the list.
 * ------------------------------------------------------------------ */

export function Workbench({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return <div className={clsx('pd-workbench', className)}>{children}</div>;
}

export function PageHeader({
  eyebrow,
  title,
  count,
  actions,
  children,
}: {
  /** Stencilled module name. Names where you are without a breadcrumb trail. */
  eyebrow?: ReactNode;
  title: ReactNode;
  /** Result count, rendered in mono beside the title. */
  count?: ReactNode;
  actions?: ReactNode;
  children?: ReactNode;
}) {
  return (
    <header className="pd-page-head">
      <div className="pd-page-head__main">
        {eyebrow ? <p className="stencil pd-page-head__eyebrow">{eyebrow}</p> : null}
        <div className="row-baseline">
          <h1 className="t-display pd-page-head__title">{title}</h1>
          {count !== undefined ? <span className="num pd-page-head__count">{count}</span> : null}
        </div>
      </div>
      {children}
      {actions ? <div className="row shrink-0" data-print="hide">{actions}</div> : null}
    </header>
  );
}

/** Filter strip under the header. Sticky, hairline-ruled, never a card. */
export function Toolbar({
  children,
  trailing,
  className,
}: {
  children?: ReactNode;
  trailing?: ReactNode;
  className?: string;
}) {
  return (
    <div className={clsx('pd-toolbar', className)} data-print="hide">
      <div className="pd-toolbar__lead">{children}</div>
      {trailing ? <div className="pd-toolbar__trail">{trailing}</div> : null}
    </div>
  );
}

/** The scrolling list region of a Workbench. */
export function ListPane({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return <div className={clsx('pd-list-pane', className)}>{children}</div>;
}

/**
 * The manifest — the detail panel that opens beside the list.
 * Named after the delivery manifest it usually contains.
 */
export function ManifestPane({
  open,
  children,
  className,
}: {
  open: boolean;
  children: ReactNode;
  className?: string;
}) {
  if (!open) return null;
  return (
    <aside className={clsx('pd-manifest', className)} aria-label="Details">
      {children}
    </aside>
  );
}

/* ---------------- Empty state ----------------
   An empty screen is an instruction, not a mood. It says what is
   missing and offers the one action that fixes it. */

export function EmptyState({
  title,
  body,
  action,
  compact = false,
}: {
  title: ReactNode;
  body?: ReactNode;
  action?: ReactNode;
  compact?: boolean;
}) {
  return (
    <div className="pd-empty" data-compact={compact || undefined}>
      <div className="pd-empty__mark" aria-hidden="true">
        {/* An empty crate, drawn in the same hairline as the rest of the UI. */}
        <svg viewBox="0 0 64 44" fill="none" stroke="currentColor" strokeWidth="1.25">
          <path d="M6 12h52v26a4 4 0 0 1-4 4H10a4 4 0 0 1-4-4V12Z" />
          <path d="M2 6h60v6H2z" />
          <path d="M18 12v30M32 12v30M46 12v30" opacity=".45" />
        </svg>
      </div>
      <h2 className="pd-empty__title">{title}</h2>
      {body ? <p className="pd-empty__body">{body}</p> : null}
      {action ? <div className="pd-empty__action">{action}</div> : null}
    </div>
  );
}

/* ---------------- Section ----------------
   Groups fields inside a detail panel. The rule under the heading is
   the structure; there is no card. */

export function Section({
  title,
  actions,
  children,
  className,
}: {
  title?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section className={clsx('pd-section', className)}>
      {title ? (
        <div className="pd-section__head">
          <h3 className="stencil pd-section__title">{title}</h3>
          {actions ? <div className="row">{actions}</div> : null}
        </div>
      ) : null}
      {children}
    </section>
  );
}

/** Label/value pairs in a detail panel. Stacked, never a fixed-width grid. */
export function FactList({
  items,
  columns = 2,
}: {
  items: { label: ReactNode; value: ReactNode; span?: boolean }[];
  columns?: 1 | 2 | 3;
}) {
  return (
    <dl className="pd-facts" data-columns={columns}>
      {items.map((item, i) => (
        <div key={i} className="pd-facts__item" data-span={item.span || undefined}>
          <dt className="stencil">{item.label}</dt>
          <dd className="pd-facts__value">{item.value}</dd>
        </div>
      ))}
    </dl>
  );
}

/* ---------------- Metric tile ----------------
   Used on the dashboard. Deliberately not a big-number-on-a-card:
   the figure is set in mono at the same weight as the tables, with
   the label stencilled above, so a dashboard reads as a summary of
   the ledger rather than as a different product. */

export function Metric({
  label,
  value,
  delta,
  footnote,
  tone,
  onClick,
}: {
  label: ReactNode;
  value: ReactNode;
  delta?: ReactNode;
  footnote?: ReactNode;
  tone?: 'ok' | 'warn' | 'alert' | 'accent' | undefined;
  onClick?: (() => void) | undefined;
}) {
  const Root = onClick ? 'button' : 'div';
  return (
    <Root
      className="pd-metric"
      data-tone={tone}
      data-interactive={onClick ? true : undefined}
      onClick={onClick}
      {...(onClick ? { type: 'button' as const } : {})}
    >
      <p className="stencil pd-metric__label">{label}</p>
      <div className="row-baseline">
        <span className="num pd-metric__value">{value}</span>
        {delta}
      </div>
      {footnote ? <p className="pd-metric__foot">{footnote}</p> : null}
    </Root>
  );
}
